/**
 * UnitsPanel — Liste détaillée des logements (avec accordéon locataires).
 *
 * Composants exportés/internes :
 *   - UnitsPanel
 *   - OccupancyChip (chip statut)
 *   - TenantContactCard (carte locataire compacte)
 *   - DetailRow (helper d'affichage)
 *
 * Extrait de AdminImmeublePortalMirror.jsx (Batch #14.221).
 */
import { useState } from "react";
import { Home, ChevronDown, ChevronUp, Mail, Phone, Send, Users, Pencil, Plus } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { PanelTitle, EmptyState } from "./_shared";
// Batch #14.226 — Édition de la répartition du loyer (multi-locataires)
import { RentSplitEditor } from "@/components/RentSplitEditor";
import { fmtCAD as fmtMoney } from "@/lib/format";

// API_BASE et auth headers pour l'admin (RentSplitEditor accepte une apiUrl + authHeaders)
const ADMIN_API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const adminAuthH = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};


// Batch #14.192 — Chip statut occupation (3 états)
function OccupancyChip({ status, isManual }) {
  const conf = status === "occupied"
    ? { cls: "bg-emerald-200 text-emerald-800 border-emerald-300", label: "🟢 Occupé" }
    : status === "transition"
    ? { cls: "bg-amber-200 text-amber-800 border-amber-300", label: "🟡 Transition" }
    : { cls: "bg-rose-200 text-rose-800 border-rose-300", label: "🔴 Vacant" };
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold border ${conf.cls}`}
      title={isManual ? "Statut manuel" : "Auto-détecté"}
    >
      {conf.label}{isManual && <span className="text-[8px] ml-0.5">(M)</span>}
    </span>
  );
}

// Petit composant pour afficher une carte contact locataire compact
function TenantContactCard({ t, compact = false }) {
  const fullName = [t.first_name, t.last_name].filter(Boolean).join(" ") || "Locataire";
  return (
    <div className={`rounded-md ${compact ? "bg-slate-50 border border-slate-200 p-2" : ""}`}>
      <div className="font-semibold text-[11px] text-slate-800 flex items-center gap-1.5">
        <Users className="w-3 h-3 text-blue-600" />
        {fullName}
        {t.is_active === false && (
          <span className="text-[9px] bg-slate-200 text-slate-600 px-1 rounded">inactif</span>
        )}
      </div>
      <div className="flex flex-col gap-0.5 mt-1 text-[10px] text-slate-600">
        {t.phone && (
          <a href={`tel:${t.phone}`} className="inline-flex items-center gap-1 hover:text-blue-600">
            <Phone className="w-3 h-3 flex-shrink-0" /> {t.phone}
          </a>
        )}
        {t.email && (
          <a href={`mailto:${t.email}`} className="inline-flex items-center gap-1 hover:text-blue-600 truncate">
            <Mail className="w-3 h-3 flex-shrink-0" /> {t.email}
          </a>
        )}
        {!t.phone && !t.email && (
          <span className="italic text-slate-400">Aucun contact renseigné</span>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="flex gap-2">
      <div className="w-24 flex-shrink-0 text-[10px] uppercase font-semibold text-slate-500">{label}</div>
      <div className="flex-1 text-slate-700">{children}</div>
    </div>
  );
}

// Batch #14.183 + #14.184 — Actions de contact manuel (filet de sécurité)
// Génère des liens mailto: avec sujet + corps pré-remplis selon le contexte
// + un bouton tel: pour appeler directement depuis mobile.
function ManualEmailActions({ tenant, ctx, fullName }) {
  const aptLabel = ctx?.unit?.unit_label || "—";
  const buildingName = ctx?.building?.name || "votre logement";
  const greeting = tenant.first_name ? `Bonjour ${tenant.first_name},` : "Bonjour,";

  const templates = [
    {
      key: "general",
      label: "Message libre",
      subject: `DG Comptabilité — ${buildingName} apt ${aptLabel}`,
      body: `${greeting}\n\n\n\nCordialement,\nDG Comptabilité`,
      tone: "slate",
    },
    {
      key: "rent_reminder",
      label: "Rappel de loyer",
      subject: `Rappel de loyer — apt ${aptLabel}`,
      body: `${greeting}\n\nCeci est un rappel concernant votre loyer pour le logement ${aptLabel} de l'immeuble ${buildingName}.\n\nMerci de communiquer avec nous dès que possible afin de régulariser la situation.\n\nCordialement,\nDG Comptabilité`,
      tone: "amber",
    },
    {
      key: "document",
      label: "Document à transmettre",
      subject: `Document — apt ${aptLabel}`,
      body: `${greeting}\n\nVeuillez trouver ci-joint le document concernant votre logement ${aptLabel} de l'immeuble ${buildingName}.\n\nN'hésitez pas à nous contacter si vous avez la moindre question.\n\nCordialement,\nDG Comptabilité`,
      tone: "blue",
    },
    {
      key: "lease",
      label: "Bail / renouvellement",
      subject: `Bail / renouvellement — apt ${aptLabel}`,
      body: `${greeting}\n\nNous souhaitons faire un suivi concernant votre bail pour le logement ${aptLabel} de l'immeuble ${buildingName}.\n\nMerci de nous revenir afin de planifier la suite.\n\nCordialement,\nDG Comptabilité`,
      tone: "emerald",
    },
  ];

  const toneClasses = {
    slate: "border-slate-300 text-slate-700 hover:bg-slate-100",
    amber: "border-amber-300 text-amber-700 hover:bg-amber-50",
    blue: "border-blue-300 text-blue-700 hover:bg-blue-50",
    emerald: "border-emerald-300 text-emerald-700 hover:bg-emerald-50",
    indigo: "border-indigo-300 text-indigo-700 hover:bg-indigo-50",
  };

  const handleClick = (tpl) => {
    if (!tenant.email) return;
    const params = new URLSearchParams({ subject: tpl.subject, body: tpl.body });
    window.location.href = `mailto:${tenant.email}?${params.toString()}`;
  };

  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`mirror-tenant-mail-actions-${tenant.id}`}>
      {/* Bouton Appeler (tel:) — Batch #14.184. Idéal mobile : clic = ouverture de l'app téléphone. */}
      {tenant.phone && (
        <a
          href={`tel:${tenant.phone}`}
          className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border bg-white transition ${toneClasses.indigo}`}
          data-testid={`mirror-tenant-call-${tenant.id}`}
          title={`Appeler ${fullName} (${tenant.phone})`}
        >
          <Phone className="w-2.5 h-2.5" />
          Appeler
        </a>
      )}
      {tenant.email && templates.map((tpl) => (
        <button
          key={tpl.key}
          type="button"
          onClick={() => handleClick(tpl)}
          className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border bg-white transition ${toneClasses[tpl.tone]}`}
          data-testid={`mirror-tenant-mail-${tenant.id}-${tpl.key}`}
          title={`Ouvre votre client mail avec : « ${tpl.subject} »`}
        >
          <Send className="w-2.5 h-2.5" />
          {tpl.label}
        </button>
      ))}
    </div>
  );
}

function UnitsPanel({ units, tenantById, onReload, onEdit, onCreate, canCreate }) {
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const setOccupancy = async (unit, newStatus) => {
    setBusyId(unit.id);
    try {
      await api.put(
        `/admin/immeuble/units/${unit.id}`,
        { occupancy_status: newStatus, occupancy_notes: unit.occupancy_notes },
      );
      toast.success(
        newStatus == null
          ? "Statut occupation : Auto-détection"
          : `Statut occupation : ${
              newStatus === "occupied" ? "Occupé" : newStatus === "vacant" ? "Vacant" : "Transition"
            }`,
      );
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2">
        <PanelTitle icon={Home} label="Logements" count={units.length} tone="emerald" inline />
        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            disabled={!canCreate}
            className="text-[11px] px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1 font-semibold shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="mirror-unit-create"
            title={canCreate ? "Ajouter un logement au nom du client" : "Créer d'abord un immeuble"}
          >
            <Plus className="w-3 h-3" /> Ajouter un logement
          </button>
        )}
      </div>
      {units.length === 0 ? (
        <EmptyState label="Aucun logement enregistré." />
      ) : (
        <ul className="space-y-1.5">
          {units.map((u) => {
            const tenantsHere = (u._tenants || []).filter((t) => t.is_active !== false);
            const activeCount = tenantsHere.length;
            // Batch #14.192 — Statut effectif 3 états
            const manualStatus = u.occupancy_status;
            const effectiveStatus =
              (["vacant", "occupied", "transition"].includes(manualStatus) && manualStatus)
              || (activeCount >= 1 ? "occupied" : "vacant");
            const isOpen = expanded === u.id;
            const L = u._lease;
            const rent = L?.rent_monthly ?? u.rent_amount;

            const cardTone =
              effectiveStatus === "occupied"  ? "bg-emerald-50/60 border-emerald-200" :
              effectiveStatus === "transition" ? "bg-amber-50/60 border-amber-200" :
              "bg-slate-50 border-slate-200";
            const iconTone =
              effectiveStatus === "occupied"  ? "bg-emerald-100 text-emerald-700" :
              effectiveStatus === "transition" ? "bg-amber-100 text-amber-700" :
              "bg-slate-200 text-slate-500";

            return (
              <li key={u.id}
                  className={`rounded-lg border ${cardTone}`}
                  data-testid={`mirror-unit-${u.id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : u.id)}
                  className="w-full flex items-start gap-3 p-2.5 text-left hover:bg-white/40 transition rounded-lg"
                  data-testid={`mirror-unit-toggle-${u.id}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconTone}`}>
                    <Home className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-slate-800">
                        Apt {u.unit_label || u.id.slice(0, 6)}
                      </span>
                      <span className="text-[10px] text-slate-500">— {u._building?.name || "—"}</span>
                      <OccupancyChip status={effectiveStatus} isManual={!!manualStatus} />
                      <span className="text-[10px] bg-cyan-100 text-cyan-700 border border-cyan-200 px-1.5 py-0.5 rounded font-semibold">
                        {activeCount} loc.
                      </span>
                    </div>
                    {effectiveStatus === "occupied" && (
                      <div className="text-[11px] text-slate-700 mt-1 tabular-nums font-semibold">
                        {rent ? `${fmtMoney(rent)} /mois` : "—"}
                        {tenantsHere.length > 0 && (
                          <span className="ml-2 font-normal text-slate-500">
                            · {tenantsHere.map(t => `${t.first_name || ""} ${t.last_name || ""}`.trim()).join(", ")}
                          </span>
                        )}
                      </div>
                    )}
                    {u.occupancy_notes && (
                      <div className="text-[11px] text-slate-500 italic mt-0.5">📝 {u.occupancy_notes}</div>
                    )}
                  </div>
                  {isOpen ? <ChevronUp className="w-3 h-3 text-slate-400 mt-1.5" /> : <ChevronDown className="w-3 h-3 text-slate-400 mt-1.5" />}
                </button>
                {isOpen && (
                  <div className="border-t border-slate-200 px-3 py-2.5 bg-white/50 space-y-2 text-[11px]" data-testid={`mirror-unit-details-${u.id}`}>
                    {/* Batch #14.218 — Bouton « Modifier » pour ouvrir le modal d'édition */}
                    {onEdit && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onEdit(u); }}
                          className="text-[10px] px-2 py-1 rounded border bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"
                          data-testid={`mirror-unit-edit-${u.id}`}
                        >
                          <Pencil className="w-3 h-3" /> Modifier ce logement
                        </button>
                      </div>
                    )}
                    {/* Batch #14.192 — Toggle rapide statut occupation côté admin */}
                    <div className="flex flex-wrap items-center gap-1 bg-slate-50 border border-slate-200 rounded p-1.5">
                      <span className="text-[10px] text-slate-500 uppercase font-bold mr-1">Forcer statut :</span>
                      {[
                        { v: null, label: "Auto" },
                        { v: "vacant", label: "Vacant" },
                        { v: "occupied", label: "Occupé" },
                        { v: "transition", label: "Transition" },
                      ].map((opt) => {
                        const active = (manualStatus || null) === opt.v;
                        return (
                          <button
                            key={opt.v || "auto"}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); if (!active && busyId !== u.id) setOccupancy(u, opt.v); }}
                            disabled={busyId === u.id || active}
                            className={`text-[10px] px-2 py-0.5 rounded border transition ${
                              active
                                ? "bg-blue-600 text-white border-blue-700"
                                : "bg-white text-slate-600 border-slate-200 hover:bg-blue-50"
                            } ${busyId === u.id ? "opacity-50" : ""}`}
                            data-testid={`mirror-unit-occupancy-${opt.v || "auto"}-${u.id}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <DetailRow label="Type">{u.unit_type || "—"}</DetailRow>
                    <DetailRow label="Loyer mensuel">{rent ? fmtMoney(rent) : "—"}</DetailRow>
                    {u._building && (
                      <DetailRow label="Adresse">
                        {[u._building.address_line, u._building.city, u._building.province, u._building.postal_code].filter(Boolean).join(", ") || "—"}
                      </DetailRow>
                    )}
                    {L && (
                      <>
                        <DetailRow label="Bail">
                          <span className="inline-flex items-center gap-1.5">
                            <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                              L.status === "active" ? "bg-emerald-100 text-emerald-700" :
                              L.status === "ended" ? "bg-slate-100 text-slate-600" :
                              "bg-amber-100 text-amber-700"
                            }`}>
                              {L.status || "—"}
                            </span>
                            {L.start_date && <span>{L.start_date} → {L.end_date || "—"}</span>}
                          </span>
                        </DetailRow>
                        {L.deposit_amount > 0 && <DetailRow label="Dépôt">{fmtMoney(L.deposit_amount)}</DetailRow>}
                        {/* Batch #14.226 — Édition de la répartition du loyer (multi-loc.) */}
                        {(L.tenant_ids || []).length > 1 && (
                          <RentSplitEditor
                            lease={L}
                            tenants={tenantsHere}
                            apiUrl={`${ADMIN_API_BASE}/admin/pms/leases/${L.id}/rent-split`}
                            authHeaders={adminAuthH}
                            onSaved={() => window.location.reload()}
                            testidPrefix={`admin-rent-split-${u.id}`}
                          />
                        )}
                      </>
                    )}
                    {tenantsHere.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Locataires actifs</div>
                        <div className="space-y-1.5">
                          {tenantsHere.map((t) => (
                            <TenantContactCard key={t.id} t={t} compact />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


export { UnitsPanel, OccupancyChip, TenantContactCard, DetailRow, ManualEmailActions, fmtMoney };
